import React from 'react';
import {
  FaShieldAlt, FaServer, FaCode, FaMapMarkerAlt, FaExternalLinkAlt,
  FaEnvelope, FaPhoneAlt, FaClock
} from 'react-icons/fa';
import { motion } from 'motion/react';
import ScrollReveal from './ScrollReveal';
import PageNavbar from './PageNavbar';
import {
  defaultViewport,
  staggerContainer,
  staggerItem,
  getRevealProps,
} from '../motion/scrollMotion';
import Footer from './Footer';
import { REGIONS, useRegion } from '../context/RegionContext';
import ServiceRequestForm from './ServiceRequestForm';
import './ContactPage.css';

const serviceAreas = [
  {
    icon: <FaShieldAlt />,
    title: 'Cybersecurity Services',
    desc: 'Penetration testing, compliance, cloud security, 24/7 SOC monitoring, incident response.',
  },
  {
    icon: <FaServer />,
    title: 'Managed IT Services',
    desc: 'Endpoint management, monitoring, backup & DR, identity & access management, cloud infrastructure.',
  },
  {
    icon: <FaCode />,
    title: 'Software Development',
    desc: 'Custom apps, AI development, secure SDLC, API integration, automation, security dashboards.',
  },
];

const ContactPage = () => {
  const { data } = useRegion();

  return (
    <div className="contact-page">
      <PageNavbar />

      {/* Hero */}
      <section className="contact-hero">
        <div className="contact-hero-background"></div>
        <div className="contact-hero-stars"></div>
        <ScrollReveal className="contact-hero-content" animateOnMount variant="fadeUp" duration={1.05}>
          <span className="contact-badge">Contact Proven InfoSec</span>
          <h1 className="contact-hero-title">
            Let's Build Something<br />
            <span className="contact-accent">Secure Together.</span>
          </h1>
          <p className="contact-hero-description">
            Whether you need cybersecurity services, managed IT support, or secure software
            development, we're ready to help. Tell us about your needs — we respond to every
            inquiry within one business day.
          </p>
        </ScrollReveal>
      </section>

      {/* Service Areas */}
      <section className="contact-services-section">
        <div className="contact-container">
          <motion.div className="contact-section-header" {...getRevealProps('fadeUp')}>
            <span className="contact-block-tag">Get in Touch</span>
            <h2 className="contact-section-title">Choose the Service Area That Fits Your Need.</h2>
            <p className="contact-section-subtitle">
              One of our specialists will reach out to schedule a consultation. We respond to all
              inquiries within one business day — guaranteed.
            </p>
          </motion.div>

          <motion.div
            className="contact-services-grid"
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
            variants={staggerContainer}
          >
            {serviceAreas.map((s, i) => (
              <motion.div className="contact-service-card" key={i} variants={staggerItem}>
                <div className="contact-service-icon">{s.icon}</div>
                <h3 className="contact-service-title">{s.title}</h3>
                <p className="contact-service-desc">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Form + Contact Info Grid */}
      <section className="contact-form-section">
        <div className="contact-container">
          <div className="contact-form-grid">
            {/* Form */}
            <motion.div className="contact-form-card" {...getRevealProps('slideLeft')}>
              <ServiceRequestForm source="Service Request (Contact page)" />
            </motion.div>

            {/* Sidebar info */}
            <motion.div className="contact-info-sidebar" {...getRevealProps('slideRight', 0.12)}>
              <div className="contact-info-card">
                <div className="contact-info-icon"><FaClock /></div>
                <h4 className="contact-info-title">Response Time</h4>
                <p className="contact-info-text">
                  We respond to all inquiries within <strong>one business day</strong> — typically much sooner.
                </p>
              </div>

              <div className="contact-info-card">
                <div className="contact-info-icon"><FaEnvelope /></div>
                <h4 className="contact-info-title">Email</h4>
                <p className="contact-info-text">
                  {data.email}<br />
                  <span className="contact-info-muted">For consultation, partnerships, careers</span>
                </p>
              </div>

              <div className="contact-info-card">
                <div className="contact-info-icon"><FaPhoneAlt /></div>
                <h4 className="contact-info-title">24/7 SOC Support</h4>
                <p className="contact-info-text">
                  Existing clients: <strong>round-the-clock</strong> incident response, monitoring, and triage.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="contact-offices-section">
        <div className="contact-container">
          <motion.div className="contact-section-header light" {...getRevealProps('fadeUp')}>
            <span className="contact-block-tag">Office Locations</span>
            <h2 className="contact-section-title light">Two Offices, One Integrated Team.</h2>
            <p className="contact-section-subtitle light">
              Service coverage across the United Kingdom and India — supporting 24/7 SOC operations
              and one business day response on every inquiry.
            </p>
          </motion.div>

          <div className="contact-offices-grid">
            <motion.div className="contact-office-card" {...getRevealProps('fadeUp')}>
              <div className="contact-office-flag">🇬🇧</div>
              <h3 className="contact-office-region">United Kingdom Office</h3>
              <div className="contact-office-address">
                <FaMapMarkerAlt className="contact-office-icon" />
                <div>
                  Unit 5, Mansfield House, 22 Northgate<br />
                  Sleaford, NG34 7DA<br />
                  United Kingdom
                </div>
              </div>
              <div className="contact-office-mapframe">
                <iframe
                  title="Proven InfoSec — United Kingdom office map"
                  src={REGIONS.uk.mapEmbed}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
              <a
                href={REGIONS.uk.map}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-office-map"
              >
                Get directions <FaExternalLinkAlt />
              </a>
            </motion.div>

            <motion.div className="contact-office-card" {...getRevealProps('fadeUp', 0.15)}>
              <div className="contact-office-flag">🇮🇳</div>
              <h3 className="contact-office-region">India Office</h3>
              <div className="contact-office-address">
                <FaMapMarkerAlt className="contact-office-icon" />
                <div>
                  KOIKKARA BUILDING MAYILADUMKUNNU<br />
                  P.O, Kizhakkambalam, Kerala 683562<br />
                  India
                </div>
              </div>
              <div className="contact-office-mapframe">
                <iframe
                  title="Proven InfoSec — India office map"
                  src={REGIONS.in.mapEmbed}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
              <a
                href={REGIONS.in.map}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-office-map"
              >
                Get directions <FaExternalLinkAlt />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
